import React, { useCallback, useEffect, useState } from "react";

import { Timer } from "lucide-react";

import Button from "../../Components/Button";
import CommonInput from "../../Components/Input";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchAdminCronSettings,
  updateAdminCronSettings,
} from "../../Store/Reducers/AdminSlice";
import "./style.scss";

const PARALLEL_COUNT_MIN = 1;
const PARALLEL_COUNT_MAX = 20;

const parseParallelCountInput = (
  raw: string
): { value: number | null; error: string } => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return {
      value: null,
      error: `Enter an integer between ${PARALLEL_COUNT_MIN} and ${PARALLEL_COUNT_MAX}.`,
    };
  }
  if (!/^-?\d+$/.test(trimmed)) {
    return {
      value: null,
      error: `Enter a whole number between ${PARALLEL_COUNT_MIN} and ${PARALLEL_COUNT_MAX}.`,
    };
  }
  const parsed = Number(trimmed);
  if (
    !Number.isInteger(parsed) ||
    parsed < PARALLEL_COUNT_MIN ||
    parsed > PARALLEL_COUNT_MAX
  ) {
    return {
      value: null,
      error: `Temporary Public Briefing Parallel Count must be between ${PARALLEL_COUNT_MIN} and ${PARALLEL_COUNT_MAX}.`,
    };
  }
  return { value: parsed, error: "" };
};

const AdminCronSettingsContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    tempPublicBriefingParallelCount: savedValue,
    loading: fetching,
    saving,
    loadError,
  } = useAppSelector(state => state.admin.cronSettings);
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const loading = fetching || (savedValue === null && !loadError);

  const loadSettings = useCallback(async () => {
    const result = await dispatch(fetchAdminCronSettings());
    if (fetchAdminCronSettings.fulfilled.match(result)) {
      setInputValue(String(result.payload));
      setError("");
    }
  }, [dispatch]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    if (error) {
      setError("");
    }
  };

  const handleSave = async () => {
    const { value, error: validationError } =
      parseParallelCountInput(inputValue);
    if (value === null) {
      setError(validationError);
      return;
    }

    setError("");
    const result = await dispatch(updateAdminCronSettings(value));
    if (updateAdminCronSettings.fulfilled.match(result)) {
      setInputValue(String(result.payload));
    } else {
      setError(result.payload || "Failed to save cron settings.");
    }
  };

  const isUnchanged =
    savedValue !== null && inputValue.trim() === String(savedValue);

  return (
    <div className="admin-cron-settings">
      <div className="admin-cron-settings__header">
        <div className="admin-cron-settings__icon">
          <Timer size={20} />
        </div>
        <div>
          <h2 className="admin-cron-settings__title">Cron Settings</h2>
          <p className="admin-cron-settings__subtitle">
            Control how the weekly Temporary Public Briefing job processes
            accounts.
          </p>
        </div>
      </div>

      {loadError ? (
        <div className="admin-cron-settings__banner" role="alert">
          <p>{loadError}</p>
          <Button variant="outline-primary" onClick={() => void loadSettings()}>
            Retry
          </Button>
        </div>
      ) : (
        <div className="admin-cron-settings__card">
          <CommonInput
            label="Temporary Public Briefing Parallel Count"
            name="temp_public_briefing_parallel_count"
            type="number"
            min={String(PARALLEL_COUNT_MIN)}
            max={String(PARALLEL_COUNT_MAX)}
            step="1"
            value={inputValue}
            onChange={handleChange}
            disabled={loading || saving}
            error={error}
            infoText="Maximum accounts generated at the same time within one organization. Remaining accounts wait for the current batch. The next organization starts only after this one finishes."
          />
          <div className="admin-cron-settings__actions">
            <Button
              variant="primary"
              onClick={() => void handleSave()}
              loading={saving}
              disabled={loading || saving || isUnchanged}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCronSettingsContainer;
